import React, { useState } from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Card,
    CardContent,
    Chip,
    IconButton,
    Alert,
    Button,
} from "@mui/material";
import { ThemeProvider, Theme, StyledEngineProvider } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import EditNoteIcon from "@mui/icons-material/EditNote";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { StepperWizard } from "../../../../core/components/wizard/StepperWizard";
import WizardStep from "../../../../core/components/wizard/WizardStep";
import { UploadBranchedStory } from "../components/UploadBranchedStory";
import { StoryMetadataForm } from "../components/StoryMetadataForm";
import { ReviewAndPublish } from "../components/ReviewAndPublish";
import { verbalTheme } from "../../../layout/themes/verbal.theme";
import {
    ValidationResult,
    ParsedStoryStructure,
    ParsedStoryPart,
    ParsedDecisionPoint,
} from "../../../../client/api/branchedStory";
import { isAutoContinueDecisionPoint } from "common/build/utils/branchedStoryUtils";
import { VCard } from "../../../../core/components/VCard";
import { PrimaryButton, SecondaryButton } from "../../../layout/Button/Button";
import { useAlert } from "../../../../contexts/alert.context";
import { MappedStoryQuestionType } from "../../../../models/modelTypes";

declare module "@mui/styles/defaultTheme" {
    // eslint-disable-next-line @typescript-eslint/no-empty-interface
    interface DefaultTheme extends Theme {}
}

type WizardMode = "choose" | "import" | "manual";

interface UploadStepData {
    storyId?: number;
    validation: ValidationResult;
    parsedStory?: ParsedStoryStructure;
}
interface MetadataStepData {
    storyName: string;
    author: string;
    synopsis?: string;
    themes?: number[];
    ageRange?: string;
    colour?: string;
    resources?: string;
    resourcesROI?: string;
}
interface WizardState {
    storyId?: number;
    validation?: ValidationResult;
    metadata?: MetadataStepData;
    parsedStory?: ParsedStoryStructure;
}

// ---- Helper functions for prompt JSON handling ----
// null added to satisfy MappedStoryQuestionType
function parsePrompts(prompt?: string | null): string[] {
    if (!prompt) return ["", "", ""];
    try {
        const arr = JSON.parse(prompt);
        return Array.isArray(arr) ? [...arr, "", "", ""].slice(0, 3) : ["", "", ""];
    } catch {
        return [prompt, "", ""];
    }
}

function buildPrompts(prompts: string[]): string {
    const filtered = prompts.filter(Boolean);
    return filtered.length > 0 ? JSON.stringify(filtered) : "";
}

// Max constraints for branched stories
const MAX_STORY_PARTS = 7;
const MAX_DECISION_POINTS = 3;
const MAX_QUESTIONS_PER_PART = 3;
const MAX_BRANCHES_PER_DP = 2;

export const CreateBranchedStoryWizard: React.FC = () => {
    const navigate = useNavigate();
    const { setAlert } = useAlert();
    const [mode, setMode] = useState<WizardMode>("choose");

    // ---- Import mode state ----
    const [importActiveStep, setImportActiveStep] = useState(0);
    const [importCompletedSteps, setImportCompletedSteps] = useState<boolean[]>([false, false, false]);

    // ---- Manual mode state ----
    const [manualActiveStep, setManualActiveStep] = useState(0);
    const [manualCompletedSteps, setManualCompletedSteps] = useState<boolean[]>([false, false, false, false, false]);

    // ---- Shared wizard state ----
    const [wizardState, setWizardState] = useState<WizardState>({});

    // ---- Import mode step handler (3 steps: Upload -> Details -> Review) ----
    const handleImportStepComplete = (stepIndex: number, data?: UploadStepData | MetadataStepData) => {
        const newCompletedSteps = [...importCompletedSteps];
        newCompletedSteps[stepIndex] = true;
        setImportCompletedSteps(newCompletedSteps);

        if (stepIndex === 0 && data && "validation" in data) {
            const uploadData = data as UploadStepData;
            setWizardState((prev) => ({
                ...prev,
                storyId: uploadData.storyId,
                validation: uploadData.validation,
                parsedStory: uploadData.parsedStory,
            }));
        } else if (stepIndex === 1 && data && "storyName" in data) {
            setWizardState((prev) => ({
                ...prev,
                metadata: data as MetadataStepData,
            }));
        }

        if (stepIndex < 2) {
            setImportActiveStep(stepIndex + 1);
        }
    };

    // ---- Manual mode: metadata step complete -> initialize parsedStory ----
    const handleManualMetadataComplete = (data: MetadataStepData) => {
        const initialParsedStory: ParsedStoryStructure = {
            title: data.storyName,
            author: data.author,
            synopsis: data.synopsis,
            ageRange: data.ageRange,
            colour: data.colour,
            resources: data.resources,
            resourcesROI: data.resourcesROI,
            parts: [
                {
                    partNumber: 1,
                    partLabel: "",
                    content: "",
                    questions: [],
                    decisionPoints: [],
                },
            ],
            maxDepth: 0,
        };

        setWizardState((prev) => ({
            ...prev,
            metadata: data,
            parsedStory: prev.parsedStory ?? initialParsedStory,
        }));

        const newCompleted = [...manualCompletedSteps];
        newCompleted[0] = true;
        setManualCompletedSteps(newCompleted);
        setManualActiveStep(1);
    };

    // ---- Shared handlers ----

    const handleParsedStoryChange = (updated: ParsedStoryStructure) => {
        setWizardState((prev) => ({ ...prev, parsedStory: updated }));
    };

    const handleWizardComplete = () => {
        navigate("/curation/branched-stories", {
            replace: true,
            state: { refresh: Date.now() },
        });
        setTimeout(() => {
            window.location.reload();
        }, 100);
    };

    // ---- Manual mode: update a specific part in parsedStory ----
    const updatePart = (partIndex: number, updater: (part: ParsedStoryPart) => ParsedStoryPart) => {
        setWizardState((prev) => {
            if (!prev.parsedStory) return prev;
            const updatedParts = [...prev.parsedStory.parts];
            updatedParts[partIndex] = updater({ ...updatedParts[partIndex] });
            return { ...prev, parsedStory: { ...prev.parsedStory, parts: updatedParts } };
        });
    };

    // ---- Manual Step 1: Story Parts ----

    const addStoryPart = () => {
        setWizardState((prev) => {
            if (!prev.parsedStory) return prev;
            const parts = prev.parsedStory.parts;
            if (parts.length >= MAX_STORY_PARTS) {
                setAlert(`Maximum of ${MAX_STORY_PARTS} story parts allowed.`, "warning");
                return prev;
            }
            const newPart: ParsedStoryPart = {
                partNumber: parts.length + 1,
                partLabel: "",
                content: "",
                questions: [],
                decisionPoints: [],
            };
            return {
                ...prev,
                parsedStory: { ...prev.parsedStory, parts: [...parts, newPart] },
            };
        });
    };

    const removeStoryPart = (partIndex: number) => {
        setWizardState((prev) => {
            if (!prev.parsedStory) return prev;
            const parts = prev.parsedStory.parts;
            if (parts.length <= 1) {
                setAlert("You must have at least one story part.", "warning");
                return prev;
            }
            const updatedParts = parts.filter((_, i) => i !== partIndex).map((p, i) => ({ ...p, partNumber: i + 1 }));
            // Clean up decision point targets that reference removed parts
            const maxPartNumber = updatedParts.length;
            const cleanedParts = updatedParts.map((p) => ({
                ...p,
                decisionPoints: (p.decisionPoints || []).map((dp) => ({
                    ...dp,
                    branches: dp.branches.map((b) => ({
                        ...b,
                        targetPartNumber: b.targetPartNumber > maxPartNumber ? maxPartNumber : b.targetPartNumber,
                    })),
                })),
            }));
            return {
                ...prev,
                parsedStory: { ...prev.parsedStory, parts: cleanedParts },
            };
        });
    };

    const renderStoryPartsStep = () => {
        const parts = wizardState.parsedStory?.parts || [];

        return (
            <Box>
                <Typography variant="h6" gutterBottom>
                    Story Parts
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                    Add up to {MAX_STORY_PARTS} story parts. Each part contains a section of narrative text.
                </Typography>

                {parts.map((part, index) => (
                    <Accordion key={index} defaultExpanded={index === 0}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box display="flex" alignItems="center" width="100%" justifyContent="space-between">
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    Part {part.partNumber}
                                    {part.partLabel ? `: ${part.partLabel}` : ""}
                                </Typography>
                                {parts.length > 1 ? (
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            removeStoryPart(index);
                                        }}
                                        title="Remove part"
                                        sx={{ mr: 1 }}
                                    >
                                        <DeleteIcon fontSize="small" color="error" />
                                    </IconButton>
                                ) : null}
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ flexDirection: "column", gap: 2 }}>
                            <TextField
                                label="Part Label (optional)"
                                value={part.partLabel || ""}
                                onChange={(e) => updatePart(index, (p) => ({ ...p, partLabel: e.target.value }))}
                                fullWidth
                                size="small"
                                sx={{ mb: 2 }}
                                placeholder="e.g. The Beginning, The Forest Path..."
                            />
                            <TextField
                                label="Content"
                                value={part.content}
                                onChange={(e) => updatePart(index, (p) => ({ ...p, content: e.target.value }))}
                                fullWidth
                                multiline
                                rows={6}
                                placeholder="Enter the narrative text for this story part..."
                            />
                        </AccordionDetails>
                    </Accordion>
                ))}

                {parts.length < MAX_STORY_PARTS ? (
                    <Box mt={2}>
                        <SecondaryButton onClick={addStoryPart} startIcon={<AddIcon />}>
                            Add Story Part
                        </SecondaryButton>
                    </Box>
                ) : (
                    <Alert severity="info" sx={{ mt: 2 }}>
                        Maximum of {MAX_STORY_PARTS} story parts reached.
                    </Alert>
                )}

                <Box display="flex" justifyContent="flex-end" mt={3} gap={2}>
                    <SecondaryButton onClick={() => setManualActiveStep(0)}>Back</SecondaryButton>
                    <PrimaryButton
                        onClick={() => {
                            // Validate at least one part has content
                            const hasContent = parts.some((p) => p.content.trim().length > 0);
                            if (!hasContent) {
                                setAlert("At least one story part must have content.", "warning");
                                return;
                            }
                            const newCompleted = [...manualCompletedSteps];
                            newCompleted[1] = true;
                            setManualCompletedSteps(newCompleted);
                            setManualActiveStep(2);
                        }}
                    >
                        Continue
                    </PrimaryButton>
                </Box>
            </Box>
        );
    };

    // ---- Manual Step 2: Decision Points ----

    const addDecisionPoint = (partIndex: number) => {
        updatePart(partIndex, (part) => {
            const dps = part.decisionPoints || [];
            if (dps.length >= 1) {
                // Only one DP per part
                setAlert("Each story part can have at most one decision point.", "warning");
                return part;
            }

            const totalDPs = (wizardState.parsedStory?.parts || []).reduce((sum, p) => {
                const realDPs = (p.decisionPoints || []).filter((dp) => !isAutoContinueDecisionPoint(dp));
                return sum + realDPs.length;
            }, 0);
            if (totalDPs >= MAX_DECISION_POINTS) {
                setAlert(`Maximum of ${MAX_DECISION_POINTS} decision points allowed across the story.`, "warning");
                return part;
            }

            const newDp: ParsedDecisionPoint = {
                decisionText: "",
                order: dps.length + 1,
                branches: [
                    { branchLabel: "A", choiceText: "", targetPartNumber: 1, order: 1 },
                    { branchLabel: "B", choiceText: "", targetPartNumber: 1, order: 2 },
                ],
            };
            return { ...part, decisionPoints: [...dps, newDp] };
        });
    };

    const removeDecisionPoint = (partIndex: number, dpIndex: number) => {
        updatePart(partIndex, (part) => {
            const dps = [...(part.decisionPoints || [])];
            dps.splice(dpIndex, 1);
            return { ...part, decisionPoints: dps };
        });
    };

    const updateDecisionPoint = (
        partIndex: number,
        dpIndex: number,
        updater: (dp: ParsedDecisionPoint) => ParsedDecisionPoint,
    ) => {
        updatePart(partIndex, (part) => {
            const dps = [...(part.decisionPoints || [])];
            dps[dpIndex] = updater({ ...dps[dpIndex] });
            return { ...part, decisionPoints: dps };
        });
    };

    const renderDecisionPointsStep = () => {
        const parts = wizardState.parsedStory?.parts || [];
        const totalDPs = parts.reduce((sum, p) => {
            const realDPs = (p.decisionPoints || []).filter((dp) => !isAutoContinueDecisionPoint(dp));
            return sum + realDPs.length;
        }, 0);

        return (
            <Box>
                <Typography variant="h6" gutterBottom>
                    Decision Points
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                    Decision points allow readers to choose between two paths in the story. You can have up to{" "}
                    {MAX_DECISION_POINTS} decision points total, each with {MAX_BRANCHES_PER_DP} branches.
                </Typography>
                <Chip
                    label={`${totalDPs} / ${MAX_DECISION_POINTS} decision points used`}
                    color={totalDPs >= MAX_DECISION_POINTS ? "error" : "default"}
                    size="small"
                    sx={{ mb: 3 }}
                />

                {parts.map((part, partIndex) => (
                    <Card key={partIndex} variant="outlined" sx={{ mb: 2 }}>
                        <CardContent>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    Part {part.partNumber}
                                    {part.partLabel ? `: ${part.partLabel}` : ""}
                                </Typography>
                                {(!part.decisionPoints || part.decisionPoints.length === 0) &&
                                totalDPs < MAX_DECISION_POINTS ? (
                                    <Button
                                        size="small"
                                        startIcon={<AddIcon />}
                                        onClick={() => addDecisionPoint(partIndex)}
                                    >
                                        Add Decision Point
                                    </Button>
                                ) : null}
                            </Box>

                            {part.decisionPoints && part.decisionPoints.length > 0
                                ? part.decisionPoints.map((dp, dpIndex) => {
                                      const isAutoContinue = isAutoContinueDecisionPoint(dp);
                                      if (isAutoContinue) {
                                          return (
                                              <Typography
                                                  key={dpIndex}
                                                  variant="body2"
                                                  color="textSecondary"
                                                  sx={{ fontStyle: "italic", mt: 2 }}
                                              >
                                                  Flows automatically to Part {dp.branches[0].targetPartNumber}.
                                              </Typography>
                                          );
                                      }
                                      return (
                                          <Box
                                              key={dpIndex}
                                              sx={{
                                                  p: 2,
                                                  mt: 1,
                                                  borderLeft: "3px solid",
                                                  borderColor: "secondary.main",
                                                  backgroundColor: "grey.50",
                                                  borderRadius: 1,
                                              }}
                                          >
                                              <Box display="flex" justifyContent="space-between" alignItems="center">
                                                  <Typography variant="caption" color="textSecondary" fontWeight={600}>
                                                      Decision Point
                                                  </Typography>
                                                  <IconButton
                                                      size="small"
                                                      onClick={() => removeDecisionPoint(partIndex, dpIndex)}
                                                      title="Remove decision point"
                                                  >
                                                      <DeleteIcon fontSize="small" color="error" />
                                                  </IconButton>
                                              </Box>
                                              <TextField
                                                  label="Decision Question"
                                                  value={dp.decisionText}
                                                  onChange={(e) =>
                                                      updateDecisionPoint(partIndex, dpIndex, (d) => ({
                                                          ...d,
                                                          decisionText: e.target.value,
                                                      }))
                                                  }
                                                  fullWidth
                                                  size="small"
                                                  multiline
                                                  sx={{ mt: 1, mb: 2 }}
                                                  placeholder="What question will the reader answer?"
                                              />

                                              {dp.branches.map((branch, bIndex) => (
                                                  <Box key={bIndex} display="flex" alignItems="center" gap={1.5} mb={1}>
                                                      <Chip
                                                          label={branch.branchLabel}
                                                          size="small"
                                                          variant="outlined"
                                                      />
                                                      <TextField
                                                          label={`Option ${branch.branchLabel}`}
                                                          value={branch.choiceText}
                                                          onChange={(e) =>
                                                              updateDecisionPoint(partIndex, dpIndex, (d) => {
                                                                  const branches = [...d.branches];
                                                                  branches[bIndex] = {
                                                                      ...branches[bIndex],
                                                                      choiceText: e.target.value,
                                                                  };
                                                                  return { ...d, branches };
                                                              })
                                                          }
                                                          size="small"
                                                          sx={{ flex: 1 }}
                                                          placeholder="Choice text..."
                                                      />
                                                      <FormControl size="small" sx={{ minWidth: 150 }}>
                                                          <InputLabel>Target Part</InputLabel>
                                                          <Select
                                                              value={branch.targetPartNumber}
                                                              label="Target Part"
                                                              onChange={(e) =>
                                                                  updateDecisionPoint(partIndex, dpIndex, (d) => {
                                                                      const branches = [...d.branches];
                                                                      branches[bIndex] = {
                                                                          ...branches[bIndex],
                                                                          targetPartNumber: Number(e.target.value),
                                                                      };
                                                                      return { ...d, branches };
                                                                  })
                                                              }
                                                          >
                                                              {parts.map((p) => (
                                                                  <MenuItem key={p.partNumber} value={p.partNumber}>
                                                                      Part {p.partNumber}
                                                                      {p.partLabel ? ` - ${p.partLabel}` : ""}
                                                                  </MenuItem>
                                                              ))}
                                                          </Select>
                                                      </FormControl>
                                                  </Box>
                                              ))}
                                          </Box>
                                      );
                                  })
                                : null}

                            {!part.decisionPoints || part.decisionPoints.length === 0 ? (
                                <Typography variant="body2" color="textSecondary" sx={{ fontStyle: "italic" }}>
                                    No decision point. This is a leaf node or will flow to the next part.
                                </Typography>
                            ) : null}
                        </CardContent>
                    </Card>
                ))}

                <Box display="flex" justifyContent="flex-end" mt={3} gap={2}>
                    <SecondaryButton onClick={() => setManualActiveStep(1)}>Back</SecondaryButton>
                    <PrimaryButton
                        onClick={() => {
                            const newCompleted = [...manualCompletedSteps];
                            newCompleted[2] = true;
                            setManualCompletedSteps(newCompleted);
                            setManualActiveStep(3);
                        }}
                    >
                        Continue
                    </PrimaryButton>
                </Box>
            </Box>
        );
    };

    // ---- Manual Step 3: Questions ----

    const addQuestion = (partIndex: number) => {
        updatePart(partIndex, (part) => {
            const questions = part.questions || [];
            if (questions.length >= MAX_QUESTIONS_PER_PART) {
                setAlert(`Maximum of ${MAX_QUESTIONS_PER_PART} questions per part.`, "warning");
                return part;
            }
            const newQ: Partial<MappedStoryQuestionType> = {
                order: questions.length + 1,
                category: "Open",
                question: "",
                keywords: "",
                prompt: "",
                hint1: "",
                explaination: "",
                keywordDesc: "",
                prepGuide: "",
            };
            return { ...part, questions: [...questions, newQ] };
        });
    };

    const removeQuestion = (partIndex: number, qIndex: number) => {
        updatePart(partIndex, (part) => {
            const questions = [...(part.questions || [])];
            questions.splice(qIndex, 1);
            // Re-order
            const reordered = questions.map((q, i) => ({ ...q, order: i + 1 }));
            return { ...part, questions: reordered };
        });
    };

    const updateQuestion = (
        partIndex: number,
        qIndex: number,
        updater: (q: Partial<MappedStoryQuestionType>) => Partial<MappedStoryQuestionType>,
    ) => {
        updatePart(partIndex, (part) => {
            const questions = [...(part.questions || [])];
            questions[qIndex] = updater({ ...questions[qIndex] });
            return { ...part, questions };
        });
    };

    const renderQuestionsStep = () => {
        const parts = wizardState.parsedStory?.parts || [];

        return (
            <Box>
                <Typography variant="h6" gutterBottom>
                    Questions
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                    Add discussion questions for each story part. Up to {MAX_QUESTIONS_PER_PART} questions per part.
                </Typography>

                {parts.map((part, partIndex) => (
                    <Card key={partIndex} variant="outlined" sx={{ mb: 3 }}>
                        <CardContent>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    Part {part.partNumber}
                                    {part.partLabel ? `: ${part.partLabel}` : ""}
                                    <Chip
                                        label={`${part.questions?.length || 0} question${(part.questions?.length || 0) !== 1 ? "s" : ""}`}
                                        size="small"
                                        sx={{ ml: 1 }}
                                    />
                                </Typography>
                                {(part.questions?.length || 0) < MAX_QUESTIONS_PER_PART ? (
                                    <Button size="small" startIcon={<AddIcon />} onClick={() => addQuestion(partIndex)}>
                                        Add Question
                                    </Button>
                                ) : null}
                            </Box>

                            {part.questions && part.questions.length > 0
                                ? part.questions.map((q, qIndex) => (
                                      <Accordion key={qIndex} sx={{ mb: 1 }}>
                                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                              <Box
                                                  display="flex"
                                                  alignItems="center"
                                                  width="100%"
                                                  justifyContent="space-between"
                                              >
                                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                      Q{qIndex + 1}: {q.question || "New Question"}
                                                  </Typography>
                                                  <IconButton
                                                      size="small"
                                                      onClick={(e) => {
                                                          e.stopPropagation();
                                                          removeQuestion(partIndex, qIndex);
                                                      }}
                                                      title="Remove question"
                                                      sx={{ mr: 1 }}
                                                  >
                                                      <DeleteIcon fontSize="small" color="error" />
                                                  </IconButton>
                                              </Box>
                                          </AccordionSummary>
                                          <AccordionDetails sx={{ flexDirection: "column", gap: 2 }}>
                                              <TextField
                                                  label="Question"
                                                  value={q.question}
                                                  onChange={(e) =>
                                                      updateQuestion(partIndex, qIndex, (qn) => ({
                                                          ...qn,
                                                          question: e.target.value,
                                                      }))
                                                  }
                                                  fullWidth
                                                  size="small"
                                              />
                                              <FormControl size="small" fullWidth sx={{ mt: 1 }}>
                                                  <InputLabel>Category</InputLabel>
                                                  <Select
                                                      value={q.category}
                                                      label="Category"
                                                      onChange={(e) =>
                                                          updateQuestion(partIndex, qIndex, (qn) => ({
                                                              ...qn,
                                                              category: e.target.value as "Open" | "Closed",
                                                          }))
                                                      }
                                                  >
                                                      <MenuItem value="Open">Open</MenuItem>
                                                      <MenuItem value="Closed">Closed</MenuItem>
                                                  </Select>
                                              </FormControl>
                                              <TextField
                                                  label="Keywords"
                                                  value={q.keywords || ""}
                                                  onChange={(e) =>
                                                      updateQuestion(partIndex, qIndex, (qn) => ({
                                                          ...qn,
                                                          keywords: e.target.value,
                                                      }))
                                                  }
                                                  fullWidth
                                                  size="small"
                                                  sx={{ mt: 1 }}
                                                  placeholder="Comma-separated keywords..."
                                              />
                                              <Typography
                                                  variant="caption"
                                                  color="textSecondary"
                                                  sx={{ mt: 2, mb: 0.5 }}
                                              >
                                                  Prompts (up to 3 follow-up prompts for discussion)
                                              </Typography>
                                              {parsePrompts(q.prompt).map((promptText, pIdx) => (
                                                  <TextField
                                                      key={pIdx}
                                                      label={`Prompt ${pIdx + 1}`}
                                                      value={promptText}
                                                      onChange={(e) => {
                                                          const current = parsePrompts(q.prompt);
                                                          current[pIdx] = e.target.value;
                                                          updateQuestion(partIndex, qIndex, (qn) => ({
                                                              ...qn,
                                                              prompt: buildPrompts(current),
                                                          }));
                                                      }}
                                                      fullWidth
                                                      size="small"
                                                      sx={{ mt: 0.5 }}
                                                  />
                                              ))}
                                              <TextField
                                                  label="Why Ask This?"
                                                  value={q.explaination || ""}
                                                  onChange={(e) =>
                                                      updateQuestion(partIndex, qIndex, (qn) => ({
                                                          ...qn,
                                                          explaination: e.target.value,
                                                      }))
                                                  }
                                                  fullWidth
                                                  size="small"
                                                  multiline
                                                  rows={2}
                                                  sx={{ mt: 1 }}
                                              />
                                              <TextField
                                                  label="Discussion Purpose"
                                                  value={q.keywordDesc || ""}
                                                  onChange={(e) =>
                                                      updateQuestion(partIndex, qIndex, (qn) => ({
                                                          ...qn,
                                                          keywordDesc: e.target.value,
                                                      }))
                                                  }
                                                  fullWidth
                                                  size="small"
                                                  multiline
                                                  rows={2}
                                                  sx={{ mt: 1 }}
                                              />
                                              <TextField
                                                  label="Delivery Guidance"
                                                  value={q.prepGuide || ""}
                                                  onChange={(e) =>
                                                      updateQuestion(partIndex, qIndex, (qn) => ({
                                                          ...qn,
                                                          prepGuide: e.target.value,
                                                      }))
                                                  }
                                                  fullWidth
                                                  size="small"
                                                  multiline
                                                  rows={2}
                                                  sx={{ mt: 1 }}
                                              />
                                              <TextField
                                                  label="Help Question"
                                                  value={q.hint1 || ""}
                                                  onChange={(e) =>
                                                      updateQuestion(partIndex, qIndex, (qn) => ({
                                                          ...qn,
                                                          hint1: e.target.value,
                                                      }))
                                                  }
                                                  fullWidth
                                                  size="small"
                                                  sx={{ mt: 1 }}
                                                  placeholder="An easier version of the question to help participants..."
                                              />
                                          </AccordionDetails>
                                      </Accordion>
                                  ))
                                : null}

                            {!part.questions || part.questions.length === 0 ? (
                                <Typography variant="body2" color="textSecondary" sx={{ fontStyle: "italic" }}>
                                    No questions added for this part.
                                </Typography>
                            ) : null}
                        </CardContent>
                    </Card>
                ))}

                <Box display="flex" justifyContent="flex-end" mt={3} gap={2}>
                    <SecondaryButton onClick={() => setManualActiveStep(2)}>Back</SecondaryButton>
                    <PrimaryButton
                        onClick={() => {
                            const newCompleted = [...manualCompletedSteps];
                            newCompleted[3] = true;
                            setManualCompletedSteps(newCompleted);
                            setManualActiveStep(4);
                        }}
                    >
                        Continue
                    </PrimaryButton>
                </Box>
            </Box>
        );
    };

    // ---- Choice screen ----
    if (mode === "choose") {
        return (
            <StyledEngineProvider injectFirst>
                <ThemeProvider theme={verbalTheme}>
                    <Box p={4}>
                        <Container maxWidth="md">
                            <Typography variant="h4" gutterBottom>
                                Create Branched Story
                            </Typography>
                            <Typography variant="body1" color="textSecondary" gutterBottom>
                                Choose how you want to create your branched story
                            </Typography>
                            <Box mt={4}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} sm={6}>
                                        <VCard>
                                            <Box
                                                p={4}
                                                display="flex"
                                                flexDirection="column"
                                                alignItems="center"
                                                textAlign="center"
                                                sx={{ minHeight: 240 }}
                                            >
                                                <CloudUploadIcon sx={{ fontSize: 48, color: "#1FAF8C", mb: 2 }} />
                                                <Typography variant="h5" gutterBottom>
                                                    Import from File
                                                </Typography>
                                                <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                                                    Upload a Word document (.docx) and we will automatically extract the
                                                    story structure, questions, and decision points.
                                                </Typography>
                                                <PrimaryButton onClick={() => setMode("import")}>
                                                    Upload DOCX File
                                                </PrimaryButton>
                                            </Box>
                                        </VCard>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <VCard>
                                            <Box
                                                p={4}
                                                display="flex"
                                                flexDirection="column"
                                                alignItems="center"
                                                textAlign="center"
                                                sx={{ minHeight: 240 }}
                                            >
                                                <EditNoteIcon sx={{ fontSize: 48, color: "#121738", mb: 2 }} />
                                                <Typography variant="h5" gutterBottom>
                                                    Create Manually
                                                </Typography>
                                                <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                                                    Build your branched story from scratch. Fill in story details, add
                                                    parts, questions, and decision points step by step.
                                                </Typography>
                                                <PrimaryButton onClick={() => setMode("manual")}>
                                                    Start Story Wizard
                                                </PrimaryButton>
                                            </Box>
                                        </VCard>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Container>
                    </Box>
                </ThemeProvider>
            </StyledEngineProvider>
        );
    }

    // ---- Import mode (existing flow) ----
    if (mode === "import") {
        return (
            <StyledEngineProvider injectFirst>
                <ThemeProvider theme={verbalTheme}>
                    <Box p={4}>
                        <Container maxWidth="md">
                            <StepperWizard
                                activeStep={importActiveStep}
                                goToStep={setImportActiveStep}
                                orientation="horizontal"
                            >
                                <WizardStep
                                    identifier={0}
                                    heading="Upload Story File"
                                    completed={importCompletedSteps[0]}
                                >
                                    <UploadBranchedStory
                                        onFileUploaded={(result) => handleImportStepComplete(0, result)}
                                        onComplete={() => handleImportStepComplete(0)}
                                    />
                                </WizardStep>
                                <WizardStep identifier={1} heading="Story Details" completed={importCompletedSteps[1]}>
                                    <StoryMetadataForm
                                        storyId={wizardState.storyId}
                                        validation={wizardState.validation}
                                        parsedStory={wizardState.parsedStory}
                                        onComplete={(data) => handleImportStepComplete(1, data)}
                                    />
                                </WizardStep>
                                <WizardStep
                                    identifier={2}
                                    heading="Review & Publish"
                                    completed={importCompletedSteps[2]}
                                >
                                    <ReviewAndPublish
                                        parsedStory={wizardState.parsedStory}
                                        metadata={wizardState.metadata}
                                        validation={wizardState.validation}
                                        onComplete={handleWizardComplete}
                                        onParsedStoryChange={handleParsedStoryChange}
                                    />
                                </WizardStep>
                            </StepperWizard>
                        </Container>
                    </Box>
                </ThemeProvider>
            </StyledEngineProvider>
        );
    }

    // ---- Manual mode (5 steps: Details -> Parts -> Decision Points -> Questions -> Review & Save) ----
    return (
        <StyledEngineProvider injectFirst>
            <ThemeProvider theme={verbalTheme}>
                <Box p={4}>
                    <Container maxWidth="md">
                        <StepperWizard
                            activeStep={manualActiveStep}
                            goToStep={(step) => {
                                // Only allow navigating to completed steps or the current+1
                                if (step <= manualActiveStep || manualCompletedSteps[step - 1]) {
                                    setManualActiveStep(step);
                                }
                            }}
                            orientation="horizontal"
                        >
                            <WizardStep identifier={0} heading="Story Details" completed={manualCompletedSteps[0]}>
                                <StoryMetadataForm
                                    parsedStory={wizardState.parsedStory}
                                    onComplete={(data) => handleManualMetadataComplete(data as MetadataStepData)}
                                />
                            </WizardStep>
                            <WizardStep identifier={1} heading="Story Parts" completed={manualCompletedSteps[1]}>
                                {renderStoryPartsStep()}
                            </WizardStep>
                            <WizardStep identifier={2} heading="Decision Points" completed={manualCompletedSteps[2]}>
                                {renderDecisionPointsStep()}
                            </WizardStep>
                            <WizardStep identifier={3} heading="Questions" completed={manualCompletedSteps[3]}>
                                {renderQuestionsStep()}
                            </WizardStep>
                            <WizardStep identifier={4} heading="Review & Save" completed={manualCompletedSteps[4]}>
                                <ReviewAndPublish
                                    parsedStory={wizardState.parsedStory}
                                    metadata={wizardState.metadata}
                                    onComplete={handleWizardComplete}
                                    onParsedStoryChange={handleParsedStoryChange}
                                />
                            </WizardStep>
                        </StepperWizard>
                    </Container>
                </Box>
            </ThemeProvider>
        </StyledEngineProvider>
    );
};
